/**
 * DSCResultsTabs.jsx
 *
 * Multi-tab nav for the DSC results section. Tab order is CANONICAL —
 * reads `DSC_TAB_ORDER` (via `getTabOrder()`) from
 * `data/dscSlotConfig.js`. Tabs with `count === 0` GREY OUT — they do NOT
 * disappear (wishlist #11).
 *
 * Citations (port-first):
 *  - Legacy LWC `searchResults.html:234-242` — the `<lightning-tabset>` block
 *    with one `<lightning-tab if:true={showDSC...} label={...} onactive={...}>`
 *    per tab. We port the multi-tab visual pattern. Legacy DOM order is wrong
 *    for DTH (bits-first); the PWA canonical order (`dscSlotConfig.js` constants
 *    `TH_TAB_ORDER`, `DTH_TAB_ORDER`) is the source of truth.
 *  - Group D `ResultsPage.jsx` tab nav pattern — desktop horizontal tabs,
 *    mobile <select>. Plan §0 row `DSCResultsTabs.jsx`: "Tab nav uses Group D
 *    `ResultsPage.jsx:1-100` pattern."
 *  - Plan §1.3: tab order is canonical. Brainstorm wishlist #11: empty tabs
 *    grey out, do not disappear.
 *  - `dscSlotConfig.js#getTabOrder(productLine, toggles)` filters out the
 *    toggle-dependent tabs (`coupling-sleeve`, `bit-adapters`) when the
 *    corresponding toggle is OFF.
 *
 * @module components/dsc/DSCResultsTabs
 * @since 2026-05-20 (Group E SP2 — shared DSC primitives)
 */

import { useDSC } from '@contexts/DSCContext';

// Display labels — canonical user-facing names, separate from the kebab-case
// slugs returned by getTabOrder(). Each label is sourced from legacy LWC
// labels (`searchResults.html:235-241`):
//   labelTHShanks, labelTHCouplingSleeve, labelTHRods, labelTHBits,
//   labelDTHBits, labelDTHHammers, labelDTHRods (=DTH Drill pipes).
// Bit Adapters is new per brainstorm §A (Vandana 19 May 2026).
export const DSC_TAB_LABELS = {
  // TH
  'shank-adapters': 'Shank adapters',
  'coupling-sleeve': 'Coupling sleeve',
  'rod-extensions': 'Rod extensions',
  'bit-adapters': 'Bit adapters',
  'th-bits': 'TH bits',
  // DTH (canonical order Hammers → Drill pipes → Bits per plan §1.3)
  'dth-hammers': 'DTH Hammers',
  'dth-drill-pipes': 'DTH Drill pipes',
  'dth-bits': 'DTH Bits',
};

/**
 * @param {Object} props
 * @param {string} props.activeTab — currently selected tab slug.
 * @param {(slug: string) => void} props.onTabChange
 * @param {Record<string, number>} [props.tabCounts] — optional per-tab counts.
 *        If omitted, falls back to `useDSC().tabCounts` (SP1 default: zeros).
 * @param {string} [props.dataTestid]
 */
export default function DSCResultsTabs({
  activeTab,
  onTabChange,
  tabCounts,
  dataTestid = 'dsc-results-tabs',
}) {
  const { tabOrder, tabCounts: ctxCounts } = useDSC();
  const counts = tabCounts || ctxCounts || {};

  if (!Array.isArray(tabOrder) || tabOrder.length === 0) {
    return null;
  }

  return (
    <div data-testid={dataTestid} className="border-b border-n3">
      {/* Mobile: <select> dropdown (Group D ResultsPage pattern). */}
      <div className="md:hidden p-2">
        <select
          value={activeTab || tabOrder[0]}
          onChange={(e) => onTabChange(e.target.value)}
          data-testid="dsc-results-tabs-mobile-select"
          className="w-full px-3 py-2 bg-bg-paper border border-n3 text-carbon-black font-sandvik-text text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-electric-blue"
        >
          {tabOrder.map((slug) => {
            const count = counts[slug] ?? 0;
            const label = DSC_TAB_LABELS[slug] || slug;
            return (
              <option key={slug} value={slug}>
                {label} ({count})
              </option>
            );
          })}
        </select>
      </div>

      {/* Desktop: horizontal tab nav. */}
      <nav
        className="hidden md:flex items-stretch gap-1"
        role="tablist"
        aria-label="Drill string configurator results tabs"
      >
        {tabOrder.map((slug) => {
          const count = counts[slug] ?? 0;
          const label = DSC_TAB_LABELS[slug] || slug;
          const isActive = activeTab === slug;
          const isEmpty = count === 0;
          return (
            <button
              key={slug}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`dsc-tab-${slug}`}
              data-state={isActive ? 'active' : isEmpty ? 'empty' : 'default'}
              onClick={() => onTabChange(slug)}
              className={`px-4 py-3 text-sm font-sandvik-text font-medium border-b-2 transition-colors focus:outline-none focus:ring-2 focus:ring-electric-blue ${
                isActive
                  ? 'border-electric-blue text-electric-blue'
                  : isEmpty
                  ? 'border-transparent text-n4'
                  : 'border-transparent text-carbon-black hover:border-n4'
              }`}
            >
              {label}
              <span
                className={`ml-2 text-xs ${
                  isActive
                    ? 'text-electric-blue'
                    : isEmpty
                    ? 'text-n4'
                    : 'text-n5'
                }`}
              >
                ({count})
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
